import { MessageCircle } from "lucide-react";
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { Bot, User, CheckCheck, Check, Clock } from 'lucide-react';
import { useChatContext } from '../hooks/useChatContext';
import { Message } from '../types';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

const ChatArea: React.FC = React.memo(() => {
  const { state } = useChatContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [state.messages, scrollToBottom]);

  const formatMessageTime = useCallback((timestamp: string) => {
    const date = parseISO(timestamp);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'HH:mm')}`;
    } else {
      return format(date, 'MMM dd, HH:mm');
    }
  }, []);

  const getMessageStatusIcon = useCallback((message: Message) => {
    switch (message.status) {
      case 'read':
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-gray-400" />;
      case 'sent':
        return <Check className="w-3 h-3 text-gray-400" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  }, []);

  const getSenderIcon = useCallback((message: Message) => {
    if (message.sender_type === 'ai') {
      return <Bot className="w-4 h-4 text-blue-500" />;
    } else if (message.sender_type === 'agent') {
      return <User className="w-4 h-4 text-green-500" />;
    } else {
      return <User className="w-4 h-4 text-gray-500" />;
    }
  }, []);

  const getSenderName = useCallback((message: Message) => {
    if (message.sender_type === 'ai') {
      return 'AI Assistant';
    } else if (message.sender_type === 'agent') {
      return 'Agent';
    } else {
      return state.activeConversation?.customer_name || 'Customer';
    }
  }, [state.activeConversation?.customer_name]);

  const getMessageBubbleClass = useCallback((message: Message) => {
    const baseClass = 'message-bubble p-3 rounded-lg max-w-xs break-words';
    
    if (message.sender_type === 'customer') {
      return `${baseClass} bg-gray-100 text-gray-900`;
    } else if (message.sender_type === 'ai') {
      return `${baseClass} bg-blue-100 text-blue-900 border border-blue-200`;
    } else {
      return `${baseClass} bg-green-100 text-green-900 border border-green-200`;
    }
  }, []);

  // Memoize the customer initial to prevent recalculation
  const customerInitial = useMemo(() => {
    return state.activeConversation?.customer_name?.charAt(0) || 'C';
  }, [state.activeConversation?.customer_name]);

  if (!state.activeConversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No conversation selected</h3>
          <p className="text-gray-500">Choose a conversation from the sidebar to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Chat Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white font-medium">
                {customerInitial}
              </span>
            </div>
            <div>
              <h2 className="font-medium text-gray-900">
                {state.activeConversation.customer_name || 'Unknown Customer'}
              </h2>
              <p className="text-sm text-gray-500">{state.activeConversation.customer_phone}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
              state.activeConversation.status === 'ai-handled' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-green-100 text-green-800'
            }`}>
              {state.activeConversation.status === 'ai-handled' ? 'AI Handling' : 'Human Agent'}
            </span>
            <span className="text-sm text-gray-500 capitalize">
              {state.activeConversation.channel}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {state.messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          state.messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.sender_type === 'customer' ? 'justify-start' : 'justify-end'
              }`}
            >
              <div className={`flex gap-2 max-w-[70%] ${
                message.sender_type === 'customer' ? 'flex-row' : 'flex-row-reverse'
              }`}>
                <div className="flex-shrink-0 mt-1">
                  {getSenderIcon(message)}
                </div>
                
                <div className={`flex flex-col ${
                  message.sender_type === 'customer' ? 'items-start' : 'items-end'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500 font-medium">
                      {getSenderName(message)}
                    </span>
                    {message.is_from_ai && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                        AI
                      </span>
                    )}
                  </div>
                  
                  <div className={getMessageBubbleClass(message)}>
                    {message.message_type === 'text' ? (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    ) : (
                      <div className="text-sm text-gray-500">
                        {message.message_type} message
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs text-gray-400">
                      {formatMessageTime(message.timestamp)}
                    </span>
                    {message.sender_type !== 'customer' && (
                      <div className="flex items-center">
                        {getMessageStatusIcon(message)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
});

export default ChatArea;
